<template>
  <div class="authoring-wrapper">
    <div class="flex h-full">
      <ProgressBar
        :stepIndex="4"
        :surveyID="surveyID"
        stepsType="map-maker"
        :conciergeAssistance="false"
      />
      <section class="wrapper">
        <a-breadcrumb class="breadcrum breadcrum--authoring">
          <template v-if="user !== null && user.SkinIdentifier !== 'pg'">
            <a-breadcrumb-item
              ><a href="/">{{ client.ClientName }}</a></a-breadcrumb-item
            >
            <a-breadcrumb-item
              ><a href="/products">Products</a></a-breadcrumb-item
            >
            <a-breadcrumb-item>Quotas</a-breadcrumb-item>
          </template>
        </a-breadcrumb>
        <div class="wrapper__inner">
          <div class="page-title">
            <icon-base
              :width="32"
              :height="33"
              :viewBox1="485"
              :viewBox2="485"
              iconName="users"
              iconColor="#4318FF"
              iconStroke="transparent"
            >
              <icon-users-quotas />
            </icon-base>
            <div class="page-title__text">
              <h1>Quotas</h1>
              <span>Configure survey quotas</span>
            </div>
          </div>
          <div class="box-wrapper position-r">
            <div class="mb-25 position-r">
              <h2>Gender Quotas</h2>

              <div class="flex input-group-wrapper gap-20">
                <div class="input-group input-group--small">
                  <label>Male</label>
                  <div class="position-r">
                    <Skeleton :borderRadius="16" v-if="loading"></Skeleton>
                    <a-input-number
                      :max="100"
                      :min="0"
                      placeholder=""
                      class="w-full"
                      :precision="0"
                      @focus="(event) => event.target.select()"
                      v-model="quotaConfig.quotaList[0].quotaValue"
                      :class="{
                        error: showQuotasError,
                      }"
                    />
                  </div>
                </div>
                <div class="input-group input-group--small">
                  <label>Female</label>
                  <div class="position-r">
                    <Skeleton :borderRadius="16" v-if="loading"></Skeleton>
                    <a-input-number
                      :max="100"
                      :min="0"
                      placeholder=""
                      class="w-full"
                      :precision="0"
                      @focus="(event) => event.target.select()"
                      v-model="quotaConfig.quotaList[1].quotaValue"
                      :class="{
                        error: showQuotasError,
                      }"
                    />
                  </div>
                </div>
              </div>
            </div>
            <p v-if="showQuotasError" class="error-message">
              Your quotas do not add up to 100%. Please adjust the highlighted
              fields.
            </p>
          </div>

          <StepNaviBar
            :nextDisable="nextDisable"
            :conciergeAssistance="true"
            @prevHandler="prevStep"
            @nextHandler="nextStep"
            stepsType="map-maker-test"
            @editable-survey-updated="handleEditableSurveyUpdate"
            @loading-survey-data="loading = $event"
            :loadingNextBtn="saveLoading"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import * as jsUtils from "@/utils/jsUtils.js";
import * as wsUtils from "@/utils/wsUtils.js";
import IconBase from "@/components/general/IconBase.vue";
import IconUsersQuotas from "@/components/icons/IconUsersQuotas.vue";
import ProgressBar from "@/components/videoAds/ProgressBar.vue";
import StepNaviBar from "@/components/videoAds/StepNaviBar1.vue";
import surveyPriceMixin from "@/mixins/surveyPriceCalculation.js";
import Skeleton from "@/components/general/Skeleton.vue";

export default {
  name: "StimuliMapMaker",
  components: {
    IconBase,
    IconUsersQuotas,
    ProgressBar,
    StepNaviBar,
    Skeleton,
  },
  props: {},
  data() {
    return {
      client: {},
      user: null,
      surveyID: null,
      loading: true,
      saveLoading: false,
      quotaConfig: {
        quotaCategory: "Gender",
        quotaList: [
          {
            id: 1,
            name: "Male",
            quotaValue: null,
          },
          {
            id: 2,
            name: "Female",
            quotaValue: null,
          },
        ],
      },
      countryId: null,
      showQuotasError: false,
    };
  },
  mixins: [surveyPriceMixin],
  computed: {
    disableNextStep() {
      return true;
    },
    nextDisable() {
      return (
        this.quotaConfig.quotaList[0].quotaValue == null ||
        this.quotaConfig.quotaList[0].quotaValue === "" ||
        this.quotaConfig.quotaList[1].quotaValue == null ||
        this.quotaConfig.quotaList[1].quotaValue === ""
      );
    },
    progressBarStatus() {
      let quotasAdded = false;
      if (
        this.quotaConfig &&
        Array.isArray(this.quotaConfig.quotaList) &&
        this.quotaConfig.quotaList.length >= 2
      ) {
        const q1 = Number(this.quotaConfig.quotaList[0].quotaValue);
        const q2 = Number(this.quotaConfig.quotaList[1].quotaValue);
        quotasAdded = q1 + q2 === 100;
      }
      return [quotasAdded];
    },
    isQuotaTotalValid() {
      const q1 = Number(this.quotaConfig.quotaList[0].quotaValue);
      const q2 = Number(this.quotaConfig.quotaList[1].quotaValue);
      return q1 + q2 === 100;
    },
  },
  watch: {
    isQuotaTotalValid(newVal) {
      if (newVal) {
        this.showQuotasError = false;
      }
    },
  },
  methods: {
    handleEditableSurveyUpdate(surveyData) {
      if (
        surveyData &&
        surveyData.SurveyConfig &&
        surveyData.SurveyConfig.CountryId
      ) {
        this.countryId = surveyData.SurveyConfig.CountryId;
      }

      if (
        surveyData &&
        Array.isArray(surveyData.quotaList) &&
        this.quotaConfig &&
        Array.isArray(this.quotaConfig.quotaList)
      ) {
        surveyData.quotaList.forEach((quotaItem) => {
          const match = this.quotaConfig.quotaList.find(
            (q) => q.id === quotaItem.quotaConfigId
          );
          if (match) {
            match.quotaValue = quotaItem.precentage;
          }
        });
      }
    },

    getQuotaConfig() {
      const model = {
        wsName: "GetQuotaConfig",
        params: {
          surveyTypeId: 107,
          countryId: this.countryId,
        },
      };

      const getQuota = new Promise((resolve) => {
        wsUtils.ApiGet(model, resolve);
      });

      getQuota.then((data) => {
        const genderQuota = data.find(
          (item) => item.quotaCategory === "Gender"
        );

        if (genderQuota) {
          const hasValues =
            this.quotaConfig &&
            this.quotaConfig.quotaList &&
            this.quotaConfig.quotaList.some(
              (item) =>
                item.quotaValue !== undefined &&
                item.quotaValue !== null &&
                item.quotaValue !== ""
            );

          if (!hasValues) {
            this.quotaConfig = genderQuota;
          }
        }
      });
    },
    prevStep() {
      this.$router.push({
        name: "stimuli-map-maker",
        params: { id: this.surveyID },
      });
    },
    nextStep() {
      const totalQuota =
        Number(this.quotaConfig.quotaList[0].quotaValue) +
        Number(this.quotaConfig.quotaList[1].quotaValue);

      if (totalQuota !== 100) {
        this.showQuotasError = true;
      } else {
        this.showQuotasError = false;
        this.saveQuotas();
      }
    },
    saveQuotas() {
      this.saveLoading = true;
      const data = {
        wsName: "SaveQuotaPercentage",
        data: {
          surveyId: this.surveyID,
          userId: this.user.EncrypteduserID,
          quotaPercentages: this.quotaConfig.quotaList.map((item) => ({
            quotaConfigId: item.id,
            percentage: item.quotaValue,
          })),
        },
      };

      const saveQuotas = new Promise((resolve) => {
        wsUtils.ApiPost(data, resolve);
      });

      saveQuotas.then((data) => {
        if (data.Success) {
          this.$router.push({
            name: "map-maker-test-launch",
            params: { id: this.surveyID },
          });
        } else {
          this.$notification["error"]({
            message: "Error!",
            description:
              "Save quotas failed. Please try again or contact our support.",
            placement: "bottomLeft",
            duration: 5,
          });
        }
        this.saveLoading = false;
      });
    },
  },
  mounted() {},
  created() {},
  activated() {
    this.showQuotasError = false;
    this.quotaConfig.quotaList[0].quotaValue = null;
    this.quotaConfig.quotaList[1].quotaValue = null;
    this.surveyID = this.$route.params.id;
    this.client = jsUtils.getCurClient();
    this.user = jsUtils.getUserInfo();
    this.getMapMakerTotalPrice();
  },
};
</script>

<style lang="scss" scoped>
.box-wrapper {
  min-height: 200px;
}
.error-message {
  color: var(--red);
  font-size: 14px;
  position: absolute;
  bottom: 2px;
  left: 24px;
  font-weight: 500;
}
</style>
